import { ReactNode, forwardRef, useId } from "react"
import CurrencyInput from "react-currency-input-field"
import FormField, { fieldControlA11yProps } from "lib/components/Form/FormField"
import { fieldBox, fieldControl, resolveFieldStatus } from "lib/components/Form/fieldStyles"

export interface InputPriceProps {
  label?: string;
  placeholder?: string;
  onChange?: (value) => void
  value?: string | number;
  name: string;
  disabled?: boolean;
  maxLength?: number;
  success?: boolean;
  error?: boolean;
  message?: string;
  /** Neutral hint under the box; replaced by `message` while `error` is set. */
  helperText?: ReactNode
  onlyBorderBottom?: boolean
  noBorder?: boolean
}

function resolveBorder(noBorder: boolean, onlyBorderBottom: boolean): "none" | "bottom" | "full" {
  if (noBorder) return "none"
  if (onlyBorderBottom) return "bottom"
  return "full"
}

/** Renders a grouped amount field; never forwards `type`, since `22.200.000` is not a valid `number` input. */
const InputPrice = forwardRef<HTMLInputElement, InputPriceProps>(({
  label, placeholder, onChange, value, name, disabled = false, maxLength, success, error, message, helperText, onlyBorderBottom = false, noBorder = false,
}: InputPriceProps, InputPriceRef) => {
  const controlId = useId()
  const status = resolveFieldStatus(error, success)
  const helper = status === "danger" && message ? message : helperText

  return (
    <FormField
      controlId={controlId}
      label={label}
      name={name}
      status={status}
      disabled={disabled}
      helperText={helperText}
      message={message}>
      <div className="relative flex-1">
        <CurrencyInput
          className={`${fieldBox({
            status,
            disabled,
            border: resolveBorder(noBorder, onlyBorderBottom),
            radius: onlyBorderBottom ? "none" : "md",
            focusMode: "self",
          })} ${fieldControl({ disabled })} placeholder:opacity-100`}
          disabled={disabled}
          ref={InputPriceRef}
          name={name}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onValueChange={(eventValue) => onChange?.(eventValue)}
          decimalSeparator=","
          groupSeparator="."
          {...fieldControlA11yProps({ controlId, status, hasHelper: Boolean(helper) })}
        />
      </div>
    </FormField>
  )
})

InputPrice.displayName = "InputPrice"

export default InputPrice
